import { useEffect, useState, useRef } from 'react';
import WorkerBuilder from '../workers/WorkerBuilder';
import isAwakeWorker from './../workers/isAwake.worker';

type WorkerInstance = Worker & {
    postMessage: (message: string) => void;
    terminate: () => void;
};

/**
 * Custom hook to monitor network connectivity and system wake state
 * @returns A tuple containing [isOnline: boolean, isAwakeFromSleep: boolean]
 * @description
 * - isOnline: Indicates if the device has internet connectivity
 * - isAwakeFromSleep: Indicates if the device has just woken up from sleep
 */
function useNetwork(): [boolean, boolean] {
    const [isOnline, setNetwork] = useState<boolean>(window.navigator.onLine);
    const [isAwakeFromSleep, setIsAwakeFromSleep] = useState<boolean>(false);
    const isAwakeWorkerRef = useRef<WorkerInstance | undefined>(undefined);

    const updateNetwork = (): void => {
        setNetwork(window.navigator.onLine);
    };

    const onAwakeWorkerMessage = ({ data: message }: MessageEvent): void => {
        setIsAwakeFromSleep(message);
    };

    useEffect(() => {
        if (!isAwakeWorkerRef.current) {
            isAwakeWorkerRef.current = new WorkerBuilder(isAwakeWorker) as WorkerInstance;
            isAwakeWorkerRef.current?.postMessage('start');
            isAwakeWorkerRef.current.onmessage = onAwakeWorkerMessage;
        }
        return () => {
            isAwakeWorkerRef.current?.postMessage('');
            isAwakeWorkerRef.current?.terminate();
        };
    }, []);

    useEffect(() => {
        window.addEventListener('offline', updateNetwork);
        window.addEventListener('online', updateNetwork);
        return () => {
            window.removeEventListener('offline', updateNetwork);
            window.removeEventListener('online', updateNetwork);
        };
    });

    return [isOnline, isAwakeFromSleep];
}

export default useNetwork;
